import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatMoney, formatDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, Search, Truck } from "lucide-react";
import { exportToExcel, exportToPdf } from "@/lib/exportUtils";

const gtDate = (iso) => {
  if (!iso) return "";
  try { return formatDate(iso); } catch { return String(iso).slice(0, 10); }
};

export default function HistorialEntregas() {
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [tipoEnvio, setTipoEnvio] = useState("all");
  const [transportista, setTransportista] = useState("all");
  const [transportistas, setTransportistas] = useState([]);

  const load = async () => {
    const [ro, rt] = await Promise.all([
      api.get("/orders", { params: { all: true } }),
      api.get("/transportistas").catch(() => ({ data: [] })),
    ]);
    setOrders(ro.data || []);
    setTransportistas(rt.data || []);
  };
  useEffect(() => { load(); }, []);

  const entregados = useMemo(() => {
    return (orders || []).filter((o) => o.estado_entrega === "entregado" && !o.anulado);
  }, [orders]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return entregados.filter((o) => {
      const ref = (o.estado_entrega_at || o.fecha_entrega || o.fecha || "").slice(0, 10);
      if (desde && ref && ref < desde) return false;
      if (hasta && ref && ref > hasta) return false;
      if (tipoEnvio !== "all" && o.envio_tipo !== tipoEnvio) return false;
      if (transportista !== "all" && (o.transportista_id || "") !== transportista) return false;
      if (!s) return true;
      return (o.numero || "").toLowerCase().includes(s) ||
             (o.cliente_nombre || "").toLowerCase().includes(s) ||
             (o.direccion || "").toLowerCase().includes(s);
    });
  }, [entregados, q, desde, hasta, tipoEnvio, transportista]);

  const rows = filtered.map((o) => ({
    numero: o.numero,
    fecha_entregado: gtDate(o.estado_entrega_at || o.fecha_entrega),
    cliente: o.cliente_nombre || "",
    direccion: o.direccion || "",
    telefono: o.cliente_telefono || "",
    envio_tipo: o.envio_tipo === "ciudad" ? "Ciudad" : "Departamento",
    transportista: o.transportista_nombre || o.piloto_nombre || "",
    total: Number(o.total || 0),
    a_cobrar: Number(o.a_cobrar || 0),
  }));

  const columns = [
    { key: "numero", label: "N° Pedido", width: 12 },
    { key: "fecha_entregado", label: "Entregado", width: 14, type: "date" },
    { key: "cliente", label: "Cliente", width: 26 },
    { key: "telefono", label: "Teléfono", width: 14 },
    { key: "direccion", label: "Dirección", width: 34 },
    { key: "envio_tipo", label: "Tipo", width: 14 },
    { key: "transportista", label: "Transportista", width: 20 },
    { key: "total", label: "Total", width: 12, type: "money" },
    { key: "a_cobrar", label: "A cobrar", width: 14, type: "money" },
  ];

  const filters = {
    Desde: desde || "—",
    Hasta: hasta || "—",
    Tipo: tipoEnvio === "all" ? "Todos" : tipoEnvio,
    Transportista: transportista === "all" ? "Todos" : (transportistas.find(t => t.id === transportista)?.nombre || transportista),
    Búsqueda: q || "—",
  };

  const excel = () => exportToExcel({ title: "Historial de Entregas", filters, columns, rows, filename: "historial-entregas" });
  const pdf = () => exportToPdf({ title: "Historial de Entregas", filters, columns, rows, filename: "historial-entregas", landscape: true });

  const totalEntregado = rows.reduce((s, r) => s + Number(r.total || 0), 0);

  return (
    <div className="space-y-6" data-testid="historial-entregas-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Logística</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl mt-1">Historial de Entregas</h1>
          <p className="text-zinc-500 mt-2 text-sm">Todos los pedidos ya entregados (fuera del pipeline activo).</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={excel} data-testid="btn-export-excel">
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
          </Button>
          <Button variant="outline" onClick={pdf} data-testid="btn-export-pdf">
            <FileText className="w-4 h-4 mr-2" /> PDF
          </Button>
        </div>
      </header>

      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Desde</label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="filter-desde" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Hasta</label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="filter-hasta" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Tipo de envío</label>
            <Select value={tipoEnvio} onValueChange={setTipoEnvio}>
              <SelectTrigger data-testid="filter-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="ciudad">Ciudad</SelectItem>
                <SelectItem value="departamento">Departamento</SelectItem>
                <SelectItem value="expreso">Expreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Transportista</label>
            <Select value={transportista} onValueChange={setTransportista}>
              <SelectTrigger data-testid="filter-transportista"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {transportistas.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Buscar</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-zinc-400" />
              <Input className="pl-8" placeholder="N°, cliente, dirección…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-q" />
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm text-zinc-500">
            {rows.length} pedidos entregados
          </div>
          <div className="text-sm font-semibold">
            Total entregado: <span className="text-blue-700">{formatMoney(totalEntregado)}</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-200 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-2 py-2">N°</th>
                <th className="text-left px-2 py-2">Entregado</th>
                <th className="text-left px-2 py-2">Cliente</th>
                <th className="text-left px-2 py-2">Dirección</th>
                <th className="text-left px-2 py-2">Tipo</th>
                <th className="text-left px-2 py-2">Transportista</th>
                <th className="text-right px-2 py-2">Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-10 text-zinc-400">
                  <Truck className="w-8 h-8 mx-auto mb-2 text-zinc-300" />
                  Sin entregas en el rango seleccionado.
                </td></tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`historial-row-${o.numero}`}>
                  <td className="px-2 py-2 font-semibold">
                    <Link to={`/pedidos/${o.id}`} className="text-[#002FA7] hover:underline">{o.numero}</Link>
                  </td>
                  <td className="px-2 py-2 text-zinc-600">{gtDate(o.estado_entrega_at || o.fecha_entrega)}</td>
                  <td className="px-2 py-2">{o.cliente_nombre}</td>
                  <td className="px-2 py-2 text-zinc-600 max-w-[280px] truncate">{o.direccion}</td>
                  <td className="px-2 py-2">
                    <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded ${o.envio_tipo === "ciudad" ? "bg-blue-100 text-blue-900" : "bg-purple-100 text-purple-900"}`}>
                      {o.envio_tipo === "ciudad" ? "Ciudad" : "Depto"}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-zinc-700">{o.transportista_nombre || o.piloto_nombre || "—"}</td>
                  <td className="px-2 py-2 text-right font-semibold">{formatMoney(o.total || 0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
